import { mapValues } from "lodash-es";

import {
  acceptHotItemStore,
  defineItemStore
} from "@editor/items/itemStore.store";

import { useConditionStore } from "@editor/items/condition/conditions.store";
import { useImageStore } from "@editor/items/image/images.store";

import type { ScenarioEndItem } from "./ScenarioEndItem";

const itemStructure: ScenarioEndItem = {
  type: "scenarioEnd",
  uid: "scenarioEnd_0",

  title: "",
  description: "",
  scenarioId: null,
  imageId: null,

  id: -1,
  docs: {},
  order: -1,
  createdAt: -1
};

export const useScenarioEndStore = defineItemStore(
  "model/scenarioEnds",
  itemStructure,
  {
    getters: {
      byScenario(): { [scenarioId: number]: ScenarioEndItem[] } {
        const grouped: { [scenarioId: number]: ScenarioEndItem[] } = {};
        for (const end of this.orderedItems) {
          if (end.scenarioId == null) continue;
          if (grouped[end.scenarioId] == null) grouped[end.scenarioId] = [];
          grouped[end.scenarioId].push(end);
        }
        return grouped;
      },
      conditionIdsByScenario(): { [scenarioId: number]: number[] } {
        return mapValues(this.byScenario, (ends: ScenarioEndItem[]) => {
          if (!ends) return [];
          const conditionStore = useConditionStore();
          const endConditions = ends.flatMap((end) =>
            conditionStore.getBySource(end)
          );
          return endConditions.map((c) => c.id);
        });
      }
    },

    events: {
      async afterDelete(end) {
        // delete connected IMAGE
        if (end.imageId != null) {
          const imageStore = useImageStore();
          await imageStore.delete({ id: end.imageId, instant: true });
        }

        const conditionStore = useConditionStore();
        const conditions = conditionStore.getBySource(end);
        if (conditions.length > 0) {
          await conditionStore.deleteMany(conditions);
        }
      }
    },

    actions: {
      async duplicate(id: number, changes: Partial<ScenarioEndItem> = {}) {
        const sourceItem = this.items[id];
        const duplicated = await this.super.duplicate(id, {
          imageId: null,
          ...changes
        });

        const conditionStore = useConditionStore();
        const conditions = conditionStore.getBySource(this.items[id]);
        for (const condition of conditions) {
          await conditionStore.duplicate(condition.id, {
            srcId: duplicated.id
          });
        }

        const imageStore = useImageStore();
        if (sourceItem.imageId != null) {
          const image = imageStore.items[sourceItem.imageId];
          if (image != null) {
            const imageCopy = await imageStore.duplicate(image.id);
            duplicated.imageId = imageCopy.id;
          }
        }

        return duplicated;
      }
    }
  }
);

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHotItemStore(useScenarioEndStore, import.meta.hot)
  );
}
